import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";

/**
 * Strip superior del Geoportal — chips clickables que sirven como
 * atajo visual a los toggles de capa del sidebar.
 *
 * iter44:
 *   - BUG 2 · usar claves i18n con plural correcto (`layer_detections`,
 *     `layer_observations`, `layer_overflow`) en lugar del hack
 *     `t("detection") + "s"` que producía "Detecciónss".
 *   - BUG 3 · cada chip alterna la capa correspondiente. Estado visual
 *     opacado cuando la capa está apagada.
 */
function Chip({ label, value, tone = "ink", testid, active = true, onClick, disabled }) {
  const tones = {
    ink: "bg-white text-salnes-ink border-salnes-stone/20",
    atlantic: "bg-salnes-atlantic text-white border-salnes-atlantic",
    emerald: "bg-salnes-emerald/10 text-salnes-emerald border-salnes-emerald/20",
    coral: "bg-salnes-coral/10 text-salnes-coral border-salnes-coral/20",
  };
  const clickable = typeof onClick === "function" && !disabled;
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      aria-pressed={clickable ? active : undefined}
      className={`inline-flex items-center gap-2 rounded-full border px-3 h-8 text-[12px] transition-all ${tones[tone]} ${
        clickable
          ? "cursor-pointer hover:shadow-sm focus-visible:outline-2 focus-visible:outline-salnes-atlantic"
          : "cursor-default"
      } ${active ? "opacity-100" : "opacity-45 saturate-50"}`}
    >
      <span className="font-medium tabular-nums">{value ?? "—"}</span>
      <span className="text-[10px] uppercase tracking-[0.16em] opacity-80">
        {label}
      </span>
    </button>
  );
}

export default function GeoKpiStrip({ kpis, updatedAt, layers, onToggleLayer }) {
  const { t } = useTranslation();
  const u = kpis?.upstream || {};
  const d = kpis?.derived || {};
  const toggle = (key) => () => onToggleLayer?.(key);
  return (
    <div
      className="flex flex-wrap items-center gap-2 px-6 py-3 border-b hairline bg-white"
      data-testid="geo-kpi-strip"
    >
      <Chip
        label={t("fase2.layer_detections")}
        value={u.total_detections}
        tone="atlantic"
        testid="geo-kpi-total"
        active={layers?.detections !== false}
        onClick={toggle("detections")}
      />
      <Chip
        label={t("fase2.layer_overflow")}
        value={u.gps_invalid_count == null ? "—" : (u.total_detections || 0) - (d.resolved_count || 0)}
        tone="coral"
        testid="geo-kpi-overflow"
        active={layers?.overflow !== false}
        onClick={toggle("overflow")}
      />
      <Chip
        label={t("fase2.status_resolved")}
        value={d.resolved_count}
        tone="emerald"
        testid="geo-kpi-resolved"
        disabled
      />
      <Chip
        label="GPS invalid"
        value={u.gps_invalid_count}
        testid="geo-kpi-gpsinvalid"
        disabled
      />
      <Chip
        label={t("fase2.layer_observations")}
        value={u.observations_count}
        testid="geo-kpi-observations"
        active={layers?.observations !== false}
        onClick={toggle("observations")}
      />
      <div className="ml-auto flex items-center gap-2 text-[11px] text-salnes-stone">
        <Activity size={13} className="text-salnes-emerald animate-pulse" />
        {t("fase2.updated")} · {updatedAt}
      </div>
    </div>
  );
}
